'use strict';
import { EventEmitter } from 'events';
import $ from 'jquery';
import Promise from 'bluebird';
import utils from '../util';
import intersection from 'lodash/intersection';
import keys from 'lodash/keys';
import validate from '../api/validation';
import flatten from 'lodash/flatten';
import isNull from 'lodash/isNull';

const localRules = {
	required: value => value !== '' && value !== false && !isNull(value),
	zip: value => {
		const ukRegEx = /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})/g;

		if (!isNaN(Number(value))) {
			return true;
		} else if (value.match(ukRegEx) && value.match(ukRegEx).length > 0) {
			return true;
		}
		return false;
	},
	phone: value => value.length > 5 && value.match(/^[0-9\s]*$/) && value.indexOf('0') === 0,
	password: value => value[0] && value[0] === value[1],
	passwordlength: value => (Array.isArray(value) ? value[0] : value).length >= 6
};

const validationMessages = {
	required: '%s on pakollinen',
	zip: 'Tarkista postinumero',
	phone: 'Syötä puhelinnumero muodossa 0401234567',
	deliverymethod: 'Valitse toimitustapa',
	password: 'Salasanat eivät täsmää',
	pickuplocation: 'Valitse noutopiste',
	passwordlength: 'Salasanan tulee olla vähintään 6 merkkiä pitkä'
};

const GENERIC_ERROR = 'Virheellinen kenttä';

export default class Validator extends EventEmitter {
	constructor($form) {
		super();
		this.$form = $form;
		this.callbacks = {};
		this.conditionalRules = {};
		this.rules = {};
		this.xhr = null;
		this.addRules(localRules);
		this.isAdminForm = false;
	}

	addRules(localRules) {
		Object.keys(localRules).forEach(name => this.addRule(
			name,
			validationMessages[name] || GENERIC_ERROR,
			localRules[name]
		));
	}

	/**
	 * Add a client side validation rule.
	 *
	 * @param {String} name
	 * @param {String} message
	 * @param {Function} callback
	 */
	addRule(name, message, callback) {
		this.rules[name] = { message, callback };
		return this;
	}

	/**
	 * @param {String} fieldName
	 * @param {Function} callback
	 * @return {Validator}
	 */
	addCallback(fieldName, callback) {
		this.callbacks[fieldName] = callback;
		return this;
	}

	/**
	 * @param {String} fieldName
	 * @param {String} rule
	 * @param {Function} condition
	 */
	addConditionalRule(fieldName, rule, condition) {
		utils.ensureProp(this.conditionalRules, fieldName, []);
		this.conditionalRules[fieldName].push({ rule, condition });
		return this;
	}


	/**
	 * @param {String} fieldName
	 * @return {Array<String>}
	 */
	getConditionalRules(fieldName, value) {
		const conditionalRules = this.conditionalRules[fieldName] || [];
		return flatten(conditionalRules
			.filter(rule => rule.condition ? rule.condition.call(null, value) : true)
			.map(rule => rule.rule));
	}

	/**
	 * Validate input field based on rules found in data-validation attribute
	 *
	 * @param {jQuery} $input
	 * @param {?Array<String>} skip do not validate these rules
	 */
	validateField($input, skip = []) {
		const fieldName = $input.attr('name');

		// Find all form elements for the current form and filter out proper one(s).
		const $inputs = this.$form.find(':input');
		const $fields = $inputs.filter(function () { return this.name === fieldName; });

		let data = this.getValidationData($fields, skip);

		const skipValidation = !data.rule.length || (data.rule.indexOf('required') === -1 && !data.value);

		if (fieldName === 'kplussa_number') {
			const form = $fields.closest('form')[0];
			const firstname = form?.querySelector('#firstname')?.value || '';
			const lastname = form?.querySelector('#lastname')?.value || '';
			const plussaPrefix = '703240';
			const plussaNumber = `${plussaPrefix}${data.value}`;

			data.value = `${plussaNumber}|${firstname}|${lastname}`;
		}

		if (skipValidation) {
			this.emit('success', $fields);
			return Promise.resolve(true);
		}
		this.emit('validate', $fields);

		return this.getValidationPromise(data).then(res => {
			const callback = this.callbacks[data.field];
			if (callback) {
				callback.call(this, $fields, res.pass || false, res.errors || []);
			}
			if (res && res.pass) {
				// validation passed
				this.emit('success', $fields);
				return { field: $fields[0], pass: true };
			}
			// validation failed
			this.emit('fail', $fields, res.errors || []);
			return { field: $fields[0], pass: false };
		}).catch(err => {
			const message = typeof err === 'string' ? err : String(err?.message || err?.statusText || err?.responseText || err || 'Validation error').trim();
			this.emit('fail', $fields, [{ field: data?.field, message }]);
			return { field: $fields[0], pass: false };
		});
	}

	/**
	 *
	 * @param {jQuery}
	 * @param {} skip
	 */
	getValidationData($input, skip = []) {
		const validation = $input.data('validation');
		const validationRules = validation ? validation.split(',') : [];
		const fieldName = $input.attr('name');

		const value = $input.map((i, field) => {
			const $field = $(field);
			return ['checkbox', 'radio'].indexOf($field.attr('type')) > -1
				? $field.is(':checked') ? $field.val() : ''
				: $field.val();
		}).get();

		[].push.apply(validationRules, this.getConditionalRules(fieldName, value));

		return {
			field: fieldName,
			value: value.length > 1 ? value : value[0],
			rule: validationRules.filter(rule => skip.indexOf(rule) === -1)
		};
	}

	/**
	 * @param {Object} data
	 * @return {Promise}
	 */
	getValidationPromise(data) {
		if (data.rule && this.canValidateClientSide(data.rule)) {
			return Promise.resolve(this.validateClientSide(data.rule, data));
		}
		return validate({ ...data, isAdminForm: this.isAdminForm });
	}

	/**
	 * @param {Array} rules
	 */
	canValidateClientSide(rules) {
		return intersection(keys(this.rules), rules).length === rules.length;
	}

	/**
	 * Validate all fields of a form.
	 *
	 * @param {JQuery} $form
	 * @param {Boolean} onlyFilledFields
	 */
	validateForm($form, onlyFilledFields) {
		const fields = $form.serializeArray()
			.map(field => {
				const $input = $form.find(`input[name="${field.name}"]`);
				return this.getValidationData($input);
			})
			.filter(data => !!data.rule.length)
			.filter(data => {
				if (onlyFilledFields) {
					return data.value !== '';
				}
				return true;
			});

		return this.getValidationPromise({ fields }).then(res => {
			if (res && res.pass) {
				fields.forEach(field => {
					const $input = $form.find(`input[name="${field.name}"]`);
					this.emit('success', $input);
				});
				return { pass: true };
			}
			else if (res.errors) {
				res.errors.forEach(error => {
					const $input = $form.find(`input[name="${error.field}"]`);
					this.emit('fail', $input, [error]);
				});
				return { pass: false, errors: res.errors }
			}
			return { pass: false };
		});
	}

	/**
	 * @param {Object[]} rules
	 * @param {Object} data
	 * @return {Object}
	 */
	validateClientSide(rules, data) {
		const errors = [];
		const pass = rules.reduce((pass, rule) => {
			const localRule = this.rules[rule];
			if (localRule && !localRule.callback.call(this, data.value)) {
				errors.push({
					field: data.field,
					message: localRule.message
				});
				return false;
			}
			return pass;
		}, true);

		return { pass, errors };
	}
}
